/**
 * 승인 상태 기계 (ADR 0028 Phase C · Issue #20)
 * --------------------------------------------------------------------------
 * 컴포넌트에서 떼어 둔 이유는 하나입니다 — **두 번 실행되지 않는다**는 성질이
 * 렌더링이 아니라 전이 규칙의 것이기 때문입니다. 여기서 값으로 확인할 수 있으면
 * 그 성질이 CSS나 이벤트 순서에 좌우되지 않습니다.
 *
 * 규칙 넷입니다.
 *
 *   검토 없이 확정 없음   `confirm:start`는 `review`에서만 나갑니다.
 *   진행 중에는 잠김      `reviewing`·`confirming`에서는 어떤 시작 이벤트도 무시됩니다.
 *   실패는 검토를 지웁니다  확정이 실패하면 그 검토를 들고 있되 **다시 확정하려면**
 *                        사용자가 한 번 더 명시적으로 눌러야 합니다.
 *   종단은 되돌아가지 않음  EXECUTED·FAILED·UNKNOWN은 닫기만 남습니다.
 */
import type { AIActionApprovalResponse } from "@k8s-dashboard/contracts";
import type { ApprovalOutcome } from "@/api/aiActions";

/** 실패 사유. 계약의 닫힌 코드이거나 "알 수 없음" 하나입니다. */
export type ApprovalErrorCode = Extract<ApprovalOutcome, { status: "error" }>["code"];

export type ApprovalState =
  | { kind: "idle" }
  | { kind: "reviewing" }
  | { kind: "review"; response: AIActionApprovalResponse }
  | { kind: "confirming"; response: AIActionApprovalResponse }
  | { kind: "settled"; response: AIActionApprovalResponse }
  /** 검토를 들고 있으면 그 검토로 다시 확정할 수 있습니다. */
  | { kind: "error"; code: ApprovalErrorCode; review?: AIActionApprovalResponse };

export type ApprovalEvent =
  | { type: "review:start" }
  | { type: "confirm:start" }
  | { type: "resolved"; response: AIActionApprovalResponse }
  | { type: "failed"; code: ApprovalErrorCode }
  | { type: "retry" }
  | { type: "dismiss" };

export const INITIAL_APPROVAL: ApprovalState = { kind: "idle" };

/** 종단 상태. 여기서는 되돌아가는 전이가 없습니다. */
function terminal(response: AIActionApprovalResponse): boolean {
  return (
    response.status === "EXECUTED" ||
    response.status === "FAILED" ||
    response.status === "UNKNOWN" ||
    response.status === "REJECTED"
  );
}

/** 진행 중인지. 화면은 이 값으로 버튼을 잠급니다(중복 클릭 방지). */
export function isBusy(state: ApprovalState): boolean {
  return state.kind === "reviewing" || state.kind === "confirming";
}

/** 다이얼로그를 열어 둘 상태인지. 검토·확정·결과·오류가 모두 창 안에 있습니다. */
export function dialogOpen(state: ApprovalState): boolean {
  return state.kind !== "idle" && state.kind !== "reviewing";
}

/** 화면이 들고 있는 마지막 검토. 없으면 undefined입니다. */
export function currentReview(state: ApprovalState): AIActionApprovalResponse | undefined {
  switch (state.kind) {
    case "review":
    case "confirming":
    case "settled":
      return state.response;
    case "error":
      return state.review;
    default:
      return undefined;
  }
}

export function approvalReducer(state: ApprovalState, event: ApprovalEvent): ApprovalState {
  if (isBusy(state) && (event.type === "review:start" || event.type === "confirm:start" || event.type === "retry")) {
    /* 진행 중에는 어떤 시작도 받지 않습니다 — 더블 클릭·엔터 연타·재렌더가
       두 번째 요청이 되는 경로를 열지 않습니다. */
    return state;
  }
  switch (event.type) {
    case "review:start":
      return state.kind === "idle" || state.kind === "error" ? { kind: "reviewing" } : state;
    case "confirm:start":
      // 확정은 **검토를 들고 있을 때만** 시작합니다.
      return state.kind === "review" ? { kind: "confirming", response: state.response } : state;
    case "resolved":
      if (terminal(event.response)) return { kind: "settled", response: event.response };
      return { kind: "review", response: event.response };
    case "failed":
      return { kind: "error", code: event.code, review: currentReview(state) };
    case "retry": {
      if (state.kind !== "error") return state;
      // 검토가 남아 있으면 그 검토로 돌아가고, 없으면 처음부터입니다.
      return state.review ? { kind: "review", response: state.review } : { kind: "idle" };
    }
    case "dismiss":
      return isBusy(state) ? state : { kind: "idle" };
    default:
      return state;
  }
}
